// Client-side signature persistence. Keyed by the admin's email so multiple
// accounts on the same browser don't collide. Kept as HTML because Gmail
// signatures are HTML and users typically paste from there.
//
// Rationale: the backend's auto-generated signature was producing malformed
// output (e.g. "name@domain.comdomain.com"). Until the backend accepts a
// user-provided signature, we store it client-side and append it to outbound
// email bodies ourselves so what the recipient sees is exactly what the user
// authored.

const KEY_PREFIX = "bellhopt.leads.signature";

function storageKey(ownerEmail) {
  const suffix = (ownerEmail || "default").trim().toLowerCase() || "default";
  return `${KEY_PREFIX}.${suffix}`;
}

function safeStorage() {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage || null;
  } catch (err) {
    return null;
  }
}

export function getSignature(ownerEmail) {
  const storage = safeStorage();
  if (!storage) return "";
  try {
    return storage.getItem(storageKey(ownerEmail)) || "";
  } catch (err) {
    return "";
  }
}

export function setSignature(ownerEmail, html) {
  const storage = safeStorage();
  if (!storage) return;
  try {
    if (!html) storage.removeItem(storageKey(ownerEmail));
    else storage.setItem(storageKey(ownerEmail), html);
  } catch (err) {
    /* ignore */
  }
}

// Suggest a reasonable starter signature based on adminInfo, if the user hasn't
// saved anything yet. Intentionally plain — no colored boxes, no tracking
// images, no "Sent from my iPhone"-style taglines that can trip spam filters.
export function defaultSignatureFromAdmin(adminInfo = {}) {
  const firstName = (adminInfo.firstName || "").trim();
  const lastName = (adminInfo.lastName || "").trim();
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  const title = (adminInfo.role || "").trim();
  const email =
    (adminInfo.gmailPrimaryAddress || adminInfo.gmail || adminInfo.email || "").trim();
  const phone = (adminInfo.phone || "").trim();

  const lines = [];
  if (fullName) lines.push(escapeHtml(fullName));
  if (title) lines.push(escapeHtml(title));
  if (phone) lines.push(escapeHtml(phone));
  if (email) lines.push(`<a href="mailto:${escapeAttr(email)}">${escapeHtml(email)}</a>`);

  if (!lines.length) return "";

  return [
    "<div>",
    lines.map((line) => `<div>${line}</div>`).join(""),
    "</div>",
  ].join("");
}

export function stripTrackingHtml(html) {
  if (!html) return html;
  // Strip 1x1 tracking pixels and any <img> with display:none
  return html
    .replace(
      /<img[^>]+(?:width=['"]?1['"]?|height=['"]?1['"]?|display:\s*none)[^>]*>/gi,
      ""
    )
    .replace(/<img[^>]+src=['"]data:image[^'">]*['"][^>]*>/gi, "");
}

// Combine a body and a signature into a single HTML document. Inserts a
// natural spacer so the signature doesn't bleed into the last paragraph.
export function appendSignature(bodyHtml, signatureHtml) {
  const body = (bodyHtml || "").trim();
  const sig = (signatureHtml || "").trim();
  if (!sig) return body;
  // Avoid double-appending if the user already pasted their signature into the body.
  if (body && body.includes(sig)) return body;
  const separator = '<div style="margin-top:16px;">--<br/></div>';
  return `${body}${body ? "<br/>" : ""}${separator}${sig}`;
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function escapeAttr(str) {
  return escapeHtml(str);
}
